/**
 * @module VideoPlayer
 * @description Robust video player with Google Drive fallback + facade/poster pattern.
 *
 * Strategy to fix mobile "black screen" bug on 400+ Google Drive videos:
 *  1. FACADE: Show a static thumbnail + play button BEFORE loading the iframe.
 *     (Google Drive preview iframes cannot autoplay without user gesture on mobile,
 *      which causes the dreaded black square. Deferring iframe load until user tap
 *      guarantees the gesture + dramatically reduces bandwidth for the whole list.)
 *  2. POSTER: Auto-derive thumbnail from Drive file id (no extra storage needed).
 *  3. AUTOPLAY: Append ?autoplay=1 to /preview URL so the video starts playing the
 *     instant the iframe loads (user already tapped, so the gesture requirement
 *     is satisfied).
 *  4. REPAINT: Force a paint flush on iframe onLoad to clear the first black frame
 *     that some mobile WebKit/Chromium builds leave behind until the first scroll.
 */
import React, { useState, useEffect, useRef } from 'react';
import { X, Maximize2, ExternalLink, Play, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

/** Extract Google Drive file id from any known URL shape. Returns null if not Drive. */
function extractGDriveId(url) {
  if (!url) return null;
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /id=([a-zA-Z0-9_-]+)/,
    /\/d\/([a-zA-Z0-9_-]+)/,
  ];
  for (const pat of patterns) {
    const m = url.match(pat);
    if (m) return m[1];
  }
  return null;
}

/**
 * Convert Google Drive URL to embeddable /preview URL with autoplay hint.
 * autoplay=1 is attached because the iframe only mounts AFTER a user tap
 * (facade pattern), so mobile browsers honor the gesture.
 */
function normalizeGDriveUrl(url) {
  const id = extractGDriveId(url);
  if (!id) return url || '';
  // usp=drivesdk yields a lighter shell on mobile
  return `https://drive.google.com/file/d/${id}/preview?autoplay=1&usp=drivesdk`;
}

/** Human-facing Drive view URL (opens in native Drive app / tab). */
function getGDriveViewUrl(url) {
  const id = extractGDriveId(url);
  return id ? `https://drive.google.com/file/d/${id}/view` : (url || '');
}

/**
 * Drive thumbnail endpoint (public, cacheable, served at CDN speed).
 * sz=w1920 gives a sharp 16:9 frame that scales down gracefully on phones.
 */
function getGDriveThumbnail(url) {
  const id = extractGDriveId(url);
  if (!id) return null;
  return `https://drive.google.com/thumbnail?id=${id}&sz=w1920`;
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  // mute=1 lets autoplay work on every mobile browser without a block prompt
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1&playsinline=1&modestbranding=1&rel=0` : null;
}

function getYouTubeThumbnail(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  return m ? `https://i.ytimg.com/vi/${m[1]}/hqdefault.jpg` : null;
}

function getVimeoEmbedUrl(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  // muted=1 gate to allow mobile autoplay
  return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1&muted=1&playsinline=1` : null;
}

function getEmbedUrl(url) {
  if (!url) return null;
  const yt = getYouTubeEmbedUrl(url);
  if (yt) return { url: yt, type: 'youtube', poster: getYouTubeThumbnail(url) };
  const vim = getVimeoEmbedUrl(url);
  if (vim) return { url: vim, type: 'vimeo', poster: null };
  if (url.includes('drive.google.com')) {
    return { url: normalizeGDriveUrl(url), type: 'gdrive', poster: getGDriveThumbnail(url) };
  }
  return { url, type: 'other', poster: null };
}

/**
 * Inline video player — facade that defers iframe until user tap.
 *
 * Iter. 411.gg (Feb 2026) — UX consolidation. Yuri report: the small
 * inline iframe renders dark/tiny on mobile (16:9 shell + Drive's own
 * letterbox = video barely visible), forcing the user through TWO more
 * clicks (our Maximize2, then Drive's own ↗) before it becomes readable.
 *
 * Fix: when the caller provides `onFullscreen`, tapping the facade play
 * button now opens the fullscreen modal DIRECTLY instead of embedding
 * a tiny in-place iframe. Callers that don't provide `onFullscreen`
 * (legacy coach tabs) still get the inline behavior for backward compat.
 *
 * Props:
 *   videoUrl          internal/primary video URL
 *   externalVideoUrl  fallback external URL
 *   thumbnailUrl      optional explicit poster (else auto-derived)
 *   name              exercise / video label
 *   onClose           close callback (hides the whole player card)
 *   onFullscreen      when present, facade tap opens fullscreen modal
 *   autoPlay          if true, skip facade and load iframe immediately
 */
export function InlineVideoPlayer({ videoUrl, externalVideoUrl, thumbnailUrl, name, onClose, onFullscreen, autoPlay = false }) {
  // If a fullscreen handler is provided, we bypass the inline iframe
  // entirely: the facade tap directly opens the fullscreen modal (which
  // is bigger, cleaner, and works reliably on mobile).
  const deferToFullscreen = !!onFullscreen && !autoPlay;
  const [playing, setPlaying] = useState(autoPlay);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [posterBroken, setPosterBroken] = useState(false);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const loadedRef = useRef(false);

  const embed = getEmbedUrl(videoUrl) || getEmbedUrl(externalVideoUrl);
  const isGDrive = embed?.type === 'gdrive';
  const rawUrl = videoUrl || externalVideoUrl;
  const viewUrl = isGDrive ? getGDriveViewUrl(rawUrl) : rawUrl;
  const poster = thumbnailUrl || embed?.poster || null;

  // Iter. 411.gg — Skip the tiny/dark inline iframe entirely when the
  // parent provides `onFullscreen`. On phones the 16:9 inline shell +
  // Drive's letterbox makes the video barely visible (Yuri report),
  // so we open the fullscreen modal on mount instead. This also
  // eliminates the double "expand" button confusion (our Maximize2 +
  // Drive's own scrubber icon).
  const forwardedToFullscreenRef = useRef(false);
  useEffect(() => {
    if (forwardedToFullscreenRef.current) return;
    if (!onFullscreen || !embed) return;
    forwardedToFullscreenRef.current = true;
    onFullscreen({ url: embed.url, name, viewUrl, poster });
    // Immediately unmount the inline shell — we don't want it flashing
    // in the background while the fullscreen modal takes over.
    if (typeof onClose === 'function') onClose();
  }, []);

  useEffect(() => {
    if (!playing) return;
    setLoaded(false);
    setError(false);
    loadedRef.current = false;

    // Fallback: if iframe doesn't fire onLoad, show error after N seconds
    const timeout = isGDrive ? 8000 : 12000;
    timeoutRef.current = setTimeout(() => {
      if (!loadedRef.current) setError(true);
    }, timeout);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [embed?.url, retryCount, isGDrive, playing]);

  const handleLoad = () => {
    loadedRef.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Force a repaint flush: toggling opacity in two rAFs clears the initial black
    // frame that mobile Chromium/WebKit sometimes leaves on Drive iframes.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setLoaded(true);
      });
    });
  };

  const handleRetry = () => {
    setError(false);
    setLoaded(false);
    loadedRef.current = false;
    setRetryCount(r => r + 1);
  };

  // No video at all → empty placeholder
  if (!embed) {
    return (
      <div className="relative w-full bg-zinc-900 rounded-lg overflow-hidden flex items-center justify-center" style={{ aspectRatio: '16 / 9' }}>
        <div className="flex flex-col items-center gap-2 text-center px-4">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
            <Play className="w-7 h-7 text-primary/30" />
          </div>
          <p className="text-[11px] text-white/40 font-medium uppercase tracking-wide">
            {name || 'Exercice'}
          </p>
          <p className="text-[9px] text-white/20">Demonstration video a venir</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/80 flex items-center justify-center text-white hover:bg-red-600/90 transition-all z-10" data-testid="close-video-btn">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full bg-zinc-900 rounded-lg overflow-hidden" data-testid="inline-video-player">
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-800 border-b border-white/10">
        <span className="text-[10px] text-white/60 uppercase tracking-widest font-medium truncate pr-2">
          {name || 'Exercice'}
        </span>
        <div className="flex gap-1.5 shrink-0">
          {onFullscreen && (
            <button
              onClick={() => onFullscreen({ url: embed.url, name, viewUrl, poster })}
              className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-primary/60 transition-all"
              data-testid="fullscreen-video-btn"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-red-600/90 transition-all"
              data-testid="close-video-btn"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="relative" style={{ aspectRatio: '16 / 9' }}>
        {/* FACADE: poster + big play button, shown until user taps.
            When `deferToFullscreen` is true (Iter. 411.gg), the tap opens
            the fullscreen modal DIRECTLY — no small inline iframe. */}
        {!playing && (
          <button
            type="button"
            onClick={() => {
              if (deferToFullscreen) {
                onFullscreen({ url: embed.url, name, viewUrl, poster });
              } else {
                setPlaying(true);
              }
            }}
            className="absolute inset-0 w-full h-full group/poster cursor-pointer"
            data-testid="video-facade-play"
            aria-label={`Lire la demo: ${name || 'exercice'}`}
          >
            {poster && !posterBroken ? (
              <img
                src={poster}
                alt={name || 'Exercise thumbnail'}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => setPosterBroken(true)}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900" />
            )}
            {/* Subtle dark overlay for contrast */}
            <div className="absolute inset-0 bg-black/25 group-hover/poster:bg-black/15 transition-colors" />
            {/* Center play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center shadow-xl ring-4 ring-white/20 group-hover/poster:scale-110 group-hover/poster:bg-primary transition-all">
                <Play className="w-7 h-7 text-primary-foreground ml-1" fill="currentColor" />
              </div>
            </div>
          </button>
        )}

        {/* Loading spinner after tap */}
        {playing && !loaded && !error && (
          <div className="absolute inset-0 flex items-center justify-center z-[1] bg-zinc-900">
            {/* Keep poster visible behind spinner so no black flash */}
            {poster && !posterBroken && (
              <img src={poster} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            )}
            <div className="relative flex flex-col items-center gap-2.5">
              <div className="w-10 h-10 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
              <span className="text-[10px] text-white/70 uppercase tracking-widest font-medium">
                Chargement...
              </span>
            </div>
          </div>
        )}

        {/* Error / Fallback state — Yuri Iter. 317: explicit Drive permission
            hint instead of generic "doesn't respond". Most "black square"
            cases on Drive are actually permission failures (file not shared
            "Anyone with the link"). */}
        {playing && error && (
          <div className="absolute inset-0 flex items-center justify-center z-[2] bg-zinc-900/95">
            <div className="flex flex-col items-center gap-3 p-4 text-center">
              <AlertTriangle className="w-8 h-8 text-amber-400/70" />
              <p className="text-xs text-white/70 max-w-[260px] leading-relaxed">
                {isGDrive
                  ? "Vidéo Drive non lisible. Probable cause : le fichier n'est pas en partage \"Tous ceux qui ont le lien\". Ouvre-le ci-dessous pour vérifier."
                  : 'La video ne charge pas correctement.'}
              </p>
              {isGDrive && (
                <p className="text-[10px] text-white/40 italic max-w-[260px]">
                  Coach : ouvre Drive → clic droit sur le fichier → Partager → "Tous ceux qui ont le lien · Lecteur"
                </p>
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleRetry} className="text-xs h-7 border-white/20 text-white hover:bg-white/10" data-testid="video-retry-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Reessayer
                </Button>
                {viewUrl && (
                  <a
                    href={viewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center h-7 px-3 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="video-external-btn"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> Ouvrir
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Iframe — mounted only after user tap */}
        {playing && (
          <iframe
            ref={iframeRef}
            key={retryCount}
            src={embed.url}
            className="absolute inset-0 w-full h-full border-0 transition-opacity duration-200"
            style={{ background: '#000', opacity: loaded ? 1 : 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            title={name || 'Exercise video'}
            loading="eager"
            onLoad={handleLoad}
            // Drive + YouTube need a permissive sandbox (or none) for playback
            sandbox={isGDrive || embed.type === 'youtube' ? undefined : 'allow-same-origin allow-scripts allow-popups allow-forms allow-presentation'}
          />
        )}

        {/* External link badge for Google Drive — Iter. 411.gg: removed.
            Google Drive's iframe already renders its own "open in Drive"
            button on top of the scrubber. Having a second one at bottom
            was confusing (Yuri report). Fullscreen modal keeps the ↗
            action as the canonical way to jump to Drive. */}
      </div>
    </div>
  );
}

/**
 * Fullscreen video modal with poster fallback.
 */
export function FullscreenVideoModal({ video, language, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const [posterBroken, setPosterBroken] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setPosterBroken(false);
  }, [video?.url]);

  useEffect(() => {
    if (!video) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [video, onClose]);

  if (!video) return null;

  const embed = getEmbedUrl(video.url);
  const isGDrive = embed?.type === 'gdrive';
  const viewUrl = video.viewUrl || (isGDrive ? getGDriveViewUrl(video.url) : video.url);
  const poster = video.poster || embed?.poster || null;

  const handleLoad = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setLoaded(true));
    });
  };

  return (
    <div
      className="fixed inset-0 z-[1500] flex flex-col items-stretch justify-center bg-black"
      onClick={onClose}
      data-testid="fullscreen-overlay"
      style={{
        height: '100dvh',
        paddingTop: 'max(env(safe-area-inset-top), 12px)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div
        className="relative flex flex-col w-full h-full sm:h-auto sm:w-[95vw] sm:max-w-6xl sm:mx-auto sm:my-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-3 py-2 sm:px-0 sm:mb-3 shrink-0">
          <h3 className="text-white font-heading text-base sm:text-xl uppercase tracking-tight truncate pr-4">
            {video.name}
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            {/* Iter. 411.gg.b — External `↗` removed. The Drive iframe
                exposes its own "open in Drive" button (top-right of the
                player); duplicating it here confused users (Yuri report).
                Only the `X` close remains as the header CTA. */}
            <button
              onClick={onClose}
              aria-label={language === 'fr' ? 'Fermer' : 'Close'}
              className="w-11 h-11 rounded-full bg-white/15 ring-1 ring-white/25 flex items-center justify-center text-white hover:bg-red-600/80 hover:ring-red-400/40 transition-colors"
              data-testid="close-fullscreen-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/*
          Iter. 411.gg — Video container now takes the full remaining
          viewport on mobile (flex-1 min-h-0) so a portrait phone gets
          the biggest possible video area instead of a 16:9 sliver.
          On tablet+ (sm:) we keep the aspect-ratio 16:9 layout because
          the extra room around is already usable.
        */}
        <div
          className="relative w-full bg-zinc-900 sm:rounded-xl overflow-hidden sm:ring-1 sm:ring-white/10 sm:shadow-2xl flex-1 min-h-0 sm:flex-none sm:aspect-video"
        >
          {/* Poster behind iframe — avoids black flash during load */}
          {poster && !posterBroken && !loaded && (
            <img
              src={poster}
              alt={video.name}
              className="absolute inset-0 w-full h-full object-contain"
              onError={() => setPosterBroken(true)}
            />
          )}
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center z-[1] pointer-events-none">
              <div className="w-10 h-10 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
            </div>
          )}
          <iframe
            src={embed?.url || video.url}
            className="absolute inset-0 w-full h-full border-0 transition-opacity duration-200"
            style={{ background: '#000', opacity: loaded ? 1 : 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="eager"
            title={video.name}
            onLoad={handleLoad}
          />
        </div>

        <div className="px-3 pt-3 shrink-0 flex justify-center">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white/15 ring-1 ring-white/25 text-white text-sm font-bold uppercase tracking-wider hover:bg-red-600/80 active:scale-95 transition-colors"
            data-testid="close-fullscreen-bottom-btn"
          >
            <X className="w-4 h-4" />
            {language === 'fr' ? 'Fermer la vidéo' : language === 'es' ? 'Cerrar el video' : 'Close video'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Video play button (for exercise rows in plans).
 */
export function VideoPlayButton({ videoUrl, externalVideoUrl, name, onClick, size = 'sm' }) {
  const hasVideo = !!(videoUrl || externalVideoUrl);
  if (!hasVideo) return null;

  const isSmall = size === 'sm';

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 rounded-full transition-colors ${
        isSmall
          ? 'px-2 py-0.5 text-[9px] bg-primary/10 text-primary hover:bg-primary/20'
          : 'px-3 py-1.5 text-xs bg-primary/15 text-primary hover:bg-primary/25'
      }`}
      title={`Voir la demo: ${name}`}
      data-testid="video-play-btn"
    >
      <Play className={isSmall ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      {!isSmall && <span>Demo</span>}
    </button>
  );
}

// Re-export helpers for unit testing / reuse
export { getGDriveThumbnail, getGDriveViewUrl, extractGDriveId, normalizeGDriveUrl, getEmbedUrl };
